import React, { useState, useEffect, useMemo } from 'react';
import MenuItem from '@mui/material/MenuItem'
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TitleBar from '../../components/TitleBar/TitleBar';
import { formatDisplayDate } from '../../utils/dateUtils';
import {
  toDisplayThresholdValue,
  toStorageThresholdValue,
  formatThresholdBound,
  isPercentageValueType,
} from '../../utils/metricThresholdUtils';
import { orgMetricThresholdService, OrgMetricThreshold, UpdateOrgMetricThreshold } from '../../services/orgMetricThresholdService';
import { getAllMetricLevels, MetricLevel } from '../../services/metricLevelService';

interface EditDialogProps {
  open: boolean;
  threshold: OrgMetricThreshold | null;
  history: OrgMetricThreshold[];
  metricLevels: MetricLevel[];
  onClose: () => void;
  onSave: (updatedThreshold: UpdateOrgMetricThreshold) => void;
}

const EditThresholdDialog: React.FC<EditDialogProps> = ({ open, threshold, history, metricLevels, onClose, onSave }) => {
  const [formData, setFormData] = useState<UpdateOrgMetricThreshold>({
    id: 0,
    name: '',
    description: '',
    review_dimension: '',
    lower_bound: 0,
    upper_bound: 0,
    metrics_type: '',
    value_type: '',
    effective_start_date: '',
  // effective_end_date: '',
    overridable: true,
    is_critical: true,
    is_dashboard_metric: true,
    target_value: undefined,
    unit_of_measure: '',
    first_applicable_metric_level_id: null,
  });


const formatThresholdDate = (date?: string | null): string => {
    if (!date) return '—';
    const safeDate = date.split('T')[0];
    return formatDisplayDate(safeDate) || '—';
  };

  // Helper to check if value type is percentage
  const isPercentage = isPercentageValueType(formData.value_type);

  useEffect(() => {
    if (threshold) {
      const displayLowerBound = toDisplayThresholdValue(threshold.lower_bound, threshold.value_type) ?? 0;
      const displayUpperBound = toDisplayThresholdValue(threshold.upper_bound, threshold.value_type) ?? 0;

      setFormData({
        id: threshold.id,
        name: threshold.name || '',
        description: threshold.description || '',
        review_dimension: threshold.review_dimension || '',
        lower_bound: displayLowerBound,
        upper_bound: displayUpperBound,
        metrics_type: threshold.metrics_type || '',
        value_type: threshold.value_type || '',
        effective_start_date: threshold.effective_start_date ?? undefined,
        effective_end_date: threshold.effective_end_date || '',
        overridable: threshold.overridable ?? true,
        is_critical: threshold.is_critical ?? true,
        is_dashboard_metric: threshold.is_dashboard_metric ?? true,
        target_value: toDisplayThresholdValue(threshold.target_value, threshold.value_type),
        unit_of_measure: threshold.unit_of_measure || '',
        first_applicable_metric_level_id: threshold.first_applicable_metric_level_id ?? null,
      });
    }
  }, [threshold]);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // const handleInputChange = (field: keyof UpdateOrgMetricThreshold, value: any) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     [field]: value
  //   }));
  // };

  const handleInputChange = <K extends keyof UpdateOrgMetricThreshold>(
    field: K,
    value: UpdateOrgMetricThreshold[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // const handleSave = () => {
  //   // onSave(formData);
  //   const { effective_end_date, ...payload } = formData;
  //   onSave(payload);
  //   onClose();
  // };

  const handleSave = () => {
    // Clear previous validation error
    setValidationError(null);

    if (!formData.effective_start_date) {
      setValidationError('Effective start date is required');
      return;
    }

    if (
      threshold?.effective_start_date &&
      formData.effective_start_date <= threshold.effective_start_date
      ) {
      setValidationError('Effective start date must be later than the current version');
      return;
    }

    // Validate lower_bound <= upper_bound
    if ((formData.lower_bound || 0) > (formData.upper_bound || 0)) {
      setValidationError('Lower bound must be less than or equal to upper bound');
      return;
    }

    // Convert display values back to stored form based on unit of measure
    const lowerBoundForBackend = toStorageThresholdValue(formData.lower_bound, formData.value_type);
    const upperBoundForBackend = toStorageThresholdValue(formData.upper_bound, formData.value_type);
    const targetValueForBackend = toStorageThresholdValue(formData.target_value, formData.value_type);

    const payload: UpdateOrgMetricThreshold = {
      id: formData.id,
      name: formData.name,
      description: formData.description,
      review_dimension: formData.review_dimension,
      lower_bound: lowerBoundForBackend,
      upper_bound: upperBoundForBackend,
      metrics_type: formData.metrics_type,
      value_type: formData.value_type,
      effective_start_date: formData.effective_start_date,
      overridable: formData.overridable,
      is_critical: formData.is_critical,
      is_dashboard_metric: formData.is_dashboard_metric,
      target_value: targetValueForBackend,
      unit_of_measure: formData.unit_of_measure?.trim() || null,
      first_applicable_metric_level_id: formData.first_applicable_metric_level_id ?? null,
    };

    onSave(payload);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Metric Threshold</DialogTitle>

      {/* <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            value={formData.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
            fullWidth
          />
          <TextField
            label="Description"
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="Review Dimension"
            value={formData.review_dimension || ''}
            onChange={(e) => handleInputChange('review_dimension', e.target.value)}
            fullWidth
            disabled
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Lower Bound"
              type="number"
              value={formData.lower_bound || ''}
              onChange={(e) => handleInputChange('lower_bound', parseFloat(e.target.value))}
              fullWidth
              inputProps={{ step: 0.01 }}
            />
            <TextField
              label="Upper Bound"
              type="number"
              value={formData.upper_bound || ''}
              onChange={(e) => handleInputChange('upper_bound', parseFloat(e.target.value))}
              fullWidth
              inputProps={{ step: 0.01 }}
            />
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={formData.overridable || false}
                onChange={(e) => handleInputChange('overridable', e.target.checked)}
              />
            }
            label="Overridable"
          />
        </Box>
      </DialogContent> */}

<DialogContent>
  {validationError && (
    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setValidationError(null)}>
      {validationError}
    </Alert>
  )}
<TextField
    fullWidth
    label="Name"
    value={formData.name}
    margin="normal"
    disabled
  />

  <TextField
    fullWidth
    label="Description"
    value={formData.description}
    margin="normal"
    onChange={(e) => handleInputChange('description', e.target.value)}
  />

  <TextField
    fullWidth
    label="Review Dimension"
    value={formData.review_dimension}
    margin="normal"
    disabled
  />

  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
    <TextField
      label={isPercentage ? "Lower Bound (%)" : "Lower Bound"}
      type="number"
      value={formData.lower_bound}
      onChange={(e) => handleInputChange('lower_bound', Number(e.target.value))}
      fullWidth
      inputProps={{ step: isPercentage ? 5 : 0.05 }}
    />

    <TextField
      label={isPercentage ? "Upper Bound (%)" : "Upper Bound"}
      type="number"
      value={formData.upper_bound}
      onChange={(e) => handleInputChange('upper_bound', Number(e.target.value))}
      fullWidth
      inputProps={{ step: isPercentage ? 5 : 0.05 }}
    />

    <TextField
      label={isPercentage ? "Target (%)" : "Target Value"}
      type="number"
      value={formData.target_value ?? ''}
      onChange={(e) => handleInputChange(
        'target_value',
        e.target.value === '' ? undefined : Number(e.target.value)
      )}
      fullWidth
      inputProps={{ step: isPercentage ? 1 : 0.05 }}
      helperText="Optional goal for LLM summaries"
    />
  </Box>

  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
    <TextField
      select
      label="Metrics Type"
      value={formData.metrics_type}
      onChange={(e) => handleInputChange('metrics_type', e.target.value)}
      fullWidth
    >
      {['Negative', 'Positive', 'Target'].map(v => (
  <MenuItem key={v} value={v}>{v}</MenuItem>
))}
    </TextField>

    <TextField
      select
      label="Value Type"
      value={formData.value_type}
      onChange={(e) => handleInputChange('value_type', e.target.value)}
      fullWidth
    >
      {['Percentage', 'Amount', 'Ratio', 'UOM', 'Ratings'].map(v => (
        <MenuItem key={v} value={v}>{v}</MenuItem>
      ))}
    </TextField>

    <TextField
      label="Unit of Measure"
      value={formData.unit_of_measure || ''}
      onChange={(e) => handleInputChange('unit_of_measure', e.target.value)}
      fullWidth
      placeholder="e.g. days, hours, FTE"
      helperText="Optional display label shown in charts and tables"
    />
  </Box>

  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
    <TextField
      type="date"
      label="Effective Start Date"
      value={formData.effective_start_date}
      onChange={(e) => handleInputChange('effective_start_date', e.target.value)}
      fullWidth
      InputLabelProps={{ shrink: true }}
    />

    <TextField
      select
      label="First Applicable Level"
      value={formData.first_applicable_metric_level_id ?? ''}
      onChange={(e) => {
        const raw = e.target.value;
        handleInputChange(
          'first_applicable_metric_level_id',
          raw === '' ? null : Number(raw)
        );
      }}
      fullWidth
      helperText="Default Any = compute at every grain on the entity. Override to start at a specific level (finer grains skipped; coarser levels roll up). Configure entity Metric Levels first."
    >
      <MenuItem value="">
        <em>Any / finest on entity</em>
      </MenuItem>
      {metricLevels.map((level) => (
        <MenuItem key={level.id} value={level.id}>
          {level.display_label || level.name}
        </MenuItem>
      ))}
    </TextField>
  </Box>

  <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
    <FormControlLabel
      control={
        <Switch
          checked={Boolean(formData.overridable)}
          onChange={(e) => handleInputChange('overridable', e.target.checked)}
        />
      }
      label="Overridable"
    />
    <FormControlLabel
      control={
        <Switch
          checked={Boolean(formData.is_critical)}
          onChange={(e) => handleInputChange('is_critical', e.target.checked)}
        />
      }
      label="Critical (trigger RAID on threshold violation)"
    />
    <FormControlLabel
      control={
        <Switch
          checked={Boolean(formData.is_dashboard_metric)}
          onChange={(e) => handleInputChange('is_dashboard_metric', e.target.checked)}
        />
      }
      label="Show on Dashboard"
    />
  </Box>

  <Dialog
  open={historyOpen}
  onClose={() => setHistoryOpen(false)}
  maxWidth="lg"
  fullWidth
>
  <DialogTitle>Metric Threshold History</DialogTitle>

  <DialogContent>
    {history.length === 0 ? (
      <Typography color="text.secondary">
        No historical versions available.
      </Typography>
    ) : (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Lower Bound</TableCell>
            <TableCell>Upper Bound</TableCell>
            <TableCell>Metrics Type</TableCell>
            <TableCell>Value Type</TableCell>
            <TableCell>Unit of Measure</TableCell>
            <TableCell>First Applicable Level</TableCell>
            <TableCell>Effective Start</TableCell>
            <TableCell>Effective End</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map(h => (
            <TableRow key={h.id}>
              <TableCell>{h.lower_bound ?? '—'}</TableCell>
              <TableCell>{h.upper_bound ?? '—'}</TableCell>
              <TableCell>{h.metrics_type ?? '—'}</TableCell>
              <TableCell>{h.value_type ?? '—'}</TableCell>
              <TableCell>{h.unit_of_measure ?? '—'}</TableCell>
              <TableCell>
                {h.first_applicable_metric_level_display_label
                  || h.first_applicable_metric_level_name
                  || 'Any / finest'}
              </TableCell>
              <TableCell>{formatThresholdDate(h.effective_start_date)}</TableCell>
              <TableCell>{formatThresholdDate(h.effective_end_date)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setHistoryOpen(false)}>Close</Button>
  </DialogActions>
</Dialog>

<Box sx={{ mt: 3 }}>
  <Button
    variant="outlined"
    fullWidth
    onClick={() => setHistoryOpen(true)}
  >
    View History
  </Button>
</Box>

</DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const MetricThresholds: React.FC = () => {
  const navigate = useNavigate();
  const [thresholds, setThresholds] = useState<OrgMetricThreshold[]>([]);
  const [metricLevels, setMetricLevels] = useState<MetricLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedThreshold, setSelectedThreshold] = useState<OrgMetricThreshold | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [history, setHistory] = useState<OrgMetricThreshold[]>([]);
  
  // Tab and search state
  const [selectedTab, setSelectedTab] = useState(0);
  const [globalSearch, setGlobalSearch] = useState('');
  const [tabSearch, setTabSearch] = useState('');

  useEffect(() => {
    loadThresholds();
    loadMetricLevels();
  }, []);

  const loadMetricLevels = async () => {
    try {
      const levels = await getAllMetricLevels();
      setMetricLevels(levels || []);
    } catch (err) {
      console.error('Error loading metric levels:', err);
    }
  };

  const loadThresholds = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await orgMetricThresholdService.getAllOrgMetricThresholds();
      setThresholds(data);
    } catch (err) {
      setError('Failed to load metric thresholds');
      console.error('Error loading thresholds:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique review dimensions for tabs
  const reviewDimensions = useMemo(() => {
    const dimensions = new Set<string>();
    thresholds.forEach(t => {
      if (t.review_dimension) {
        dimensions.add(t.review_dimension);
      }
    });
    return Array.from(dimensions).sort();
  }, [thresholds]);

  // Filter thresholds based on global search (across all tabs)
  const globalFilteredThresholds = useMemo(() => {
    if (!globalSearch.trim()) return null;
    const searchLower = globalSearch.toLowerCase();
    return thresholds.filter(t => 
      t.name?.toLowerCase().includes(searchLower) ||
      t.description?.toLowerCase().includes(searchLower)
    );
  }, [thresholds, globalSearch]);

  // Filter thresholds for current tab
  const currentTabThresholds = useMemo(() => {
    if (globalFilteredThresholds) {
      // If global search is active, show all matching results
      return globalFilteredThresholds;
    }
    
    const currentDimension = reviewDimensions[selectedTab];
    if (!currentDimension) return [];
    
    let filtered = thresholds.filter(t => t.review_dimension === currentDimension);
    
    // Apply tab-specific search
    if (tabSearch.trim()) {
      const searchLower = tabSearch.toLowerCase();
      filtered = filtered.filter(t => 
        t.name?.toLowerCase().includes(searchLower) ||
        t.description?.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [thresholds, reviewDimensions, selectedTab, tabSearch, globalFilteredThresholds]);

  const handleEdit = (threshold: OrgMetricThreshold) => {
    setSelectedThreshold(threshold);
    setEditDialogOpen(true);
  };

  useEffect(() => {
    if (selectedThreshold) {
      orgMetricThresholdService
        .getOrgMetricThresholdHistory(
          selectedThreshold.name || '',
          selectedThreshold.id
        )
        .then(setHistory)
        .catch(() => setHistory([]));
    }
  }, [selectedThreshold]);

  const handleSave = async (updatedThreshold: UpdateOrgMetricThreshold) => {
    try {
      await orgMetricThresholdService.updateOrgMetricThreshold(updatedThreshold);
      setSuccess('Metric threshold updated successfully');
      await loadThresholds();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update metric threshold');
      console.error('Error updating threshold:', err);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setTabSearch(''); // Clear tab search when switching tabs
  };

  const handleGlobalSearchChange = (value: string) => {
    setGlobalSearch(value);
    if (value.trim()) {
      setTabSearch(''); // Clear tab search when using global search
    }
  };

  const formatBound = formatThresholdBound;

  const getReviewDimensionColor = (dimension?: string | null): string => {
    const colors: Record<string, string> = {
      'Schedule': '#1976d2',
      'Effort': '#388e3c',
      'Scope': '#f57c00',
      'Cost': '#d32f2f',
      'Quality': '#7b1fa2',
      'Engineering': '#0288d1',
      'Service Configuration Management': '#00897b'
    };
    return colors[dimension ?? ''] || '#757575';
  };

  // Render the metrics table
  const renderMetricsTable = (metricsToShow: OrgMetricThreshold[], showDimensionColumn: boolean = false) => (
    <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', maxWidth: 200 }}>Description</TableCell>
            {showDimensionColumn && (
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Dimension</TableCell>
            )}
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Lower</TableCell>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Upper</TableCell>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Value Type</TableCell>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>UoM</TableCell>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>First Level</TableCell>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Override</TableCell>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Critical</TableCell>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Dashboard</TableCell>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Target</TableCell>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 60 }}>Edit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {metricsToShow.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showDimensionColumn ? 14 : 13} align="center">
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No metrics found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            metricsToShow.map((threshold) => (
              <TableRow key={threshold.id} hover>
                <TableCell>
                  <Tooltip title={threshold.name || ''} arrow>
                    <Typography variant="body2" sx={{ maxWidth: 180 }} noWrap>
                      {threshold.name}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Tooltip title={threshold.description || ''} arrow>
                    <Typography variant="body2" noWrap>
                      {threshold.description}
                    </Typography>
                  </Tooltip>
                </TableCell>
                {showDimensionColumn && (
                  <TableCell>
                    <Chip 
                      label={threshold.review_dimension ?? '_'} 
                      size="small"
                      sx={{ 
                        backgroundColor: getReviewDimensionColor(threshold.review_dimension),
                        color: 'white',
                        fontSize: '0.7rem'
                      }}
                    />
                  </TableCell>
                )}
                <TableCell>{formatBound(threshold.lower_bound, threshold.value_type)}</TableCell>
                <TableCell>{formatBound(threshold.upper_bound, threshold.value_type)}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontSize="0.8rem">
                    {threshold.metrics_type || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontSize="0.8rem">
                    {threshold.value_type || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontSize="0.8rem">
                    {threshold.unit_of_measure || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontSize="0.8rem">
                    {threshold.first_applicable_metric_level_display_label
                      || threshold.first_applicable_metric_level_name
                      || 'Any'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={threshold.overridable ? 'Yes' : 'No'} 
                    color={threshold.overridable ? 'success' : 'default'}
                    size="small"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={threshold.is_critical ? 'Yes' : 'No'} 
                    color={threshold.is_critical ? 'error' : 'default'}
                    size="small"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={(threshold.is_dashboard_metric ?? true) ? 'Yes' : 'No'} 
                    color={(threshold.is_dashboard_metric ?? true) ? 'primary' : 'default'}
                    size="small"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontSize="0.8rem">
                    {threshold.target_value != null ? formatBound(threshold.target_value, threshold.value_type) : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton 
                    onClick={() => handleEdit(threshold)}
                    size="small"
                    color="primary"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box>
        <TitleBar />
        <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <TitleBar />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton 
            onClick={() => navigate('/organization-setup')} 
            aria-label="Back"
            sx={{
              border: '1px solid #ccc',
              borderRadius: 2,
              p: 1,
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#e0e0e0' },
              mr: 2
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Organization Metric Thresholds
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Configure organization-wide metric thresholds. Metric names are generic (no Project/Milestone prefixes);
          level is chosen at compute time via entity Metric Levels. First Applicable Level defaults to Any (all grains);
          override to start at a specific level (e.g. Release) when needed.
        </Typography>

        {/* Global Search - searches across all tabs */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search across all dimensions..."
            value={globalSearch}
            onChange={(e) => handleGlobalSearchChange(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: globalSearch && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setGlobalSearch('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {globalSearch && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Found {globalFilteredThresholds?.length || 0} metrics matching "{globalSearch}" across all dimensions
            </Typography>
          )}
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Paper elevation={3}>
          {/* Show global search results or tabbed view */}
          {globalSearch ? (
            // Global search results - show all matching metrics with dimension column
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Search Results
              </Typography>
              {renderMetricsTable(currentTabThresholds, true)}
            </Box>
          ) : (
            // Tabbed view by review dimension
            <>
              {/* Scrollable Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={selectedTab} 
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      minWidth: 120,
                      fontWeight: 500
                    }
                  }}
                >
                  {reviewDimensions.map((dimension, index) => {
                    const count = thresholds.filter(t => t.review_dimension === dimension).length;
                    return (
                      <Tab 
                        key={dimension} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{dimension}</span>
                            <Chip 
                              label={count} 
                              size="small" 
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem',
                                backgroundColor: getReviewDimensionColor(dimension),
                                color: 'white'
                              }} 
                            />
                          </Box>
                        }
                        id={`metric-tab-${index}`}
                        aria-controls={`metric-tabpanel-${index}`}
                      />
                    );
                  })}
                </Tabs>
              </Box>

              {/* Tab Content */}
              <Box sx={{ p: 2 }}>
                {/* Tab-specific search */}
                <TextField
                  fullWidth
                  placeholder={`Search within ${reviewDimensions[selectedTab] || 'this dimension'}...`}
                  value={tabSearch}
                  onChange={(e) => setTabSearch(e.target.value)}
                  size="small"
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: tabSearch && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setTabSearch('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                {/* Metrics count */}
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Showing {currentTabThresholds.length} metrics
                  {tabSearch && ` matching "${tabSearch}"`}
                </Typography>

                {/* Metrics Table */}
                {renderMetricsTable(currentTabThresholds, false)}
              </Box>
            </>
          )}
        </Paper>

        <EditThresholdDialog
          open={editDialogOpen}
          threshold={selectedThreshold}
          history={history}
          metricLevels={metricLevels}
          onClose={() => setEditDialogOpen(false)}
          onSave={handleSave}
        />
      </Container>
    </Box>
  );
};

export default MetricThresholds;
